import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Animatable from "react-native-animatable";
import { Box, MainButton, PressableOpacity } from "app/components/common";
import selectLanguage from "@microdoing/shared/utils/selectLanguage";
import { useDispatch, useSelector } from "react-redux";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import ActionText from "app/scenes/ActionScreen/ActionText";
import i18n from "@microdoing/shared/i18n";
import isActionEmpty from "@microdoing/shared/utils/isActionEmpty";
import {
  getDoneSteps,
  getSelectionRequesting,
} from "@microdoing/shared/services/userCampaign/selectors";
import { MainTitle } from "app/components/common/MainTitle";
import SocialSheetInfos from "./SocialSheetInfos";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import CardLabel from "./CardLabel";
import {
  Gesture,
  GestureDetector,
  ScrollView,
} from "react-native-gesture-handler";
import {
  isIos,
  isWeb,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";

import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { Dimensions, ImageBackground, StatusBar, View } from "react-native";
import CardInfos from "./CardInfos";
import { selectStep } from "@microdoing/shared/services/userCampaign/actions";
import { changeSeeActionAgain } from "@microdoing/shared/services/action/actions";
import { withAnimated } from "app/utils/withAnimated";
import { useNavigation } from "@react-navigation/native";

let { width, height } = Dimensions.get("window");

const CARD_BORDER_RADIUS = 16;

const MODAL_PADDING = "l";

const springConfig = {
  mass: 1,
  stiffness: 200,
  overshootClamping: false,
  restSpeedThreshold: 0.001,
  restDisplacementThreshold: 0.001,
};

const AnimatedBox = isWeb ? Box : withAnimated(Box);

const getNumberOfDone = (doneSteps, stepId) => {
  return doneSteps.filter(step => step.step === stepId).length;
};

const FullScreenCard = ({
  index = "",
  onPressButton,
  buttonText,
  item,
  isDisabled = false,
  activeSlide,
  onPressSocialSheet = () => null,
}) => {
  const {
    skills,
    content = { title: {} },
    title = {},
    rating,
    totalDone,
    numberOfComments = 0,
    users = [],
  } = item;

  const stepId = item._id;

  const dispatch = useDispatch();

  const handleOnPressSocialSheet = () => {
    if (isWebDesktop) {
      return setIsSocialSheetOpen(true);
    }
    onPressSocialSheet();
  };

  const doneSteps = useSelector(getDoneSteps);
  const language = useSelector(getLanguage);
  const selectionRequesting = useSelector(getSelectionRequesting);

  const numberOfDone = useCallback(getNumberOfDone(doneSteps, stepId), [
    doneSteps,
    stepId,
  ]);

  const contentTitle = selectLanguage({ text: content.title, language });
  const contentWhy = selectLanguage({ text: content.why, language });
  const contentHow = selectLanguage({ text: content.how, language });
  const actionTitle = selectLanguage({ text: title, language });

  const navigation = useNavigation();

  const [isSocialSheetOpen, setIsSocialSheetOpen] = useState(false);

  const originCardScale = useSharedValue(1);

  const imageRef = useRef(null);

  useEffect(() => {
    if (activeSlide == index) {
      dispatch(changeSeeActionAgain(stepId));
      dispatch(selectStep({ stepSelected: item }));
    }
  }, [index, activeSlide, item]);

  const imageBackground = useMemo(() => {
    try {
      if (index == "0") {
        return require("../../../../../assets/images/BackgroundGradient.png");
      } else if (index == "1") {
        return require("../../../../../assets/images/BackgroundGradient3.png");
      } else {
        return require("../../../../../assets/images/BackgroundGradient2.png");
      }
    } catch (e) {
      console.log(e);
    }
  }, [index]);

  const handleCardTouchOnJS = () => {
    navigation.navigate("Action", {
      isComingFromSelectAction: true,
      isComingFromAnalysis: false,
      cardIndex: index,
    });
  };

  const handleCardTouch = () => {
    "worklet";
    runOnJS(handleCardTouchOnJS)();
  };

  const tapGesture = Gesture.Tap()
    .onBegin(() => {
      originCardScale.value = withTiming(0.97);
    })
    .runOnJS(true)
    .onStart(() => {
      originCardScale.value = withSpring(1, springConfig, handleCardTouch);
    })
    .onFinalize(() => {
      originCardScale.value = withSpring(1, springConfig);
    })
    .onEnd(() => {
      originCardScale.value = withSpring(1, springConfig);
    })
    .onTouchesCancelled(() => {
      originCardScale.value = withSpring(1, springConfig);
    });

  const longPressGesture = Gesture.LongPress()
    .onBegin(() => {})
    .runOnJS(true)
    .onStart(() => {
      originCardScale.value = withTiming(0.97);
    })
    .onFinalize(() => {
      originCardScale.value = withSpring(1, springConfig);
    })
    .onEnd(() => {
      originCardScale.value = withSpring(1, springConfig, handleCardTouch);
    })
    .onTouchesCancelled(() => {
      originCardScale.value = withSpring(1, springConfig);
    });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: originCardScale.value,
        },
      ],
    };
  });

  return (
    <>
      <StatusBar animated backgroundColor="transparent" />
      <Animatable.View
        testID={`card-${index}`}
        animation="fadeInRight"
        style={{
          flex: 1,
          paddingTop: !isWebDesktop && theme.spacing.s,
          paddingBottom: !isWebDesktop && theme.spacing.m,
          paddingHorizontal: !isWebDesktop && theme.spacing.xxs,
          height: "100%",
          minWidth: isWebDesktop ? normalize(296) : normalize(200),
          zIndex: 1,
        }}
      >
        {numberOfDone > 0 && <CardLabel label={i18n.t("already-done")} />}
        <AnimatedBox
          style={animatedStyle}
          maxWidth={400}
          minWidth={isWebDesktop ? 400 : 0}
          overflow="hidden"
          shadowColor="shadow"
          shadowRadius={10}
          shadowOpacity={1}
          shadowOffset={{
            height: 4,
            width: 0,
          }}
          borderRadius={CARD_BORDER_RADIUS}
          height="100%"
          position="relative"
          backgroundColor="neutral.white"
          flex={1}
          zIndex={1}
        >
          <GestureDetector
            gesture={Gesture.Exclusive(tapGesture, longPressGesture)}
          >
            <ScrollView
              bounces={false}
              scrollsToTop
              style={{
                height: "100%",
                flex: 1,
                flexDirection: "column",
                paddingBottom: theme.spacing.xs,
              }}
              contentContainerStyle={{ paddingBottom: theme.spacing.xxl }}
              showsVerticalScrollIndicator={false}
            >
              <View collapsable={false}>
                <ImageBackground
                  source={imageBackground}
                  style={{
                    paddingHorizontal: theme.spacing[MODAL_PADDING],
                    paddingVertical: theme.spacing.m,
                  }}
                  pointerEvents="none"
                  colors={[
                    theme.colors["gradient.blueGradient.start"],
                    theme.colors["gradient.blueGradient.end"],
                  ]}
                >
                  <MainTitle
                    variant="l6-bold"
                    color="neutral.white"
                    marginRight="xxl"
                  >
                    {!!actionTitle ? actionTitle : "Action " + (index + 1)}
                  </MainTitle>
                  <CardInfos
                    skill={selectLanguage({
                      text: skills?.[0]?.label,
                      language,
                    })}
                  />
                  <MainTitle
                    style={{ fontSize: 18 }}
                    variant="l5-bold"
                    color="neutral.white"
                    marginBottom="xs"
                  >
                    {i18n.t("challenge")}
                  </MainTitle>
                  <ActionText
                    textStyle={{ color: "white" }}
                    text={contentTitle ? contentTitle : " "}
                  />
                </ImageBackground>
              </View>

              <Box paddingHorizontal={MODAL_PADDING}>
                {!isActionEmpty(contentWhy) && (
                  <>
                    <MainTitle
                      marginTop="sm"
                      variant="l5-medium"
                      marginBottom="xs"
                    >
                      {i18n.t("why")}
                    </MainTitle>
                    <ActionText text={contentWhy} />
                  </>
                )}

                {!isActionEmpty(contentHow) && (
                  <>
                    <MainTitle
                      variant="l5-medium"
                      marginTop="sm"
                      marginBottom="xs"
                    >
                      {i18n.t("concrete-example")}
                    </MainTitle>
                    <ActionText text={contentHow} />
                  </>
                )}
              </Box>
            </ScrollView>
          </GestureDetector>
          <Box
            zIndex={2}
            backgroundColor="neutral.50"
            position="relative"
            width="100%"
            paddingTop="m"
            paddingHorizontal="sl"
            borderRadius={CARD_BORDER_RADIUS}
            borderTopRightRadius={0}
            borderTopLeftRadius={0}
            shadowColor="neutral.50"
            shadowOffset={{ width: 0, height: -30 }}
            shadowOpacity={1}
            shadowRadius={20}
          >
            <PressableOpacity onPress={handleOnPressSocialSheet}>
              <Box marginBottom="sm">
                <SocialSheetInfos
                  contentTitle={contentTitle}
                  users={users}
                  numberOfComments={numberOfComments}
                  onPressSocialSheet={handleOnPressSocialSheet}
                  rating={rating}
                  totalDone={totalDone}
                  sheetOpen={isWebDesktop && isSocialSheetOpen}
                />
              </Box>
            </PressableOpacity>
            <Box
              paddingBottom="xm"
              justifyContent="space-between"
              flexDirection="row"
              alignItems="center"
              backgroundColor="neutral.50"
            >
              <MainButton
                margin="null"
                testID={`select-action-button-${index}`}
                isLoading={selectionRequesting}
                isDisabled={isDisabled}
                onPress={onPressButton}
                text={buttonText}
                colors={
                  index == 0
                    ? [
                        theme.colors["gradient.blueGradient.start"],
                        theme.colors["gradient.blueGradient.end"],
                      ]
                    : index == 1
                    ? [
                        theme.colors["gradient.greenGradient.start"],
                        theme.colors["gradient.greenGradient.end"],
                      ]
                    : [
                        theme.colors["gradient.orangeGradient.start"],
                        theme.colors["gradient.orangeGradient.end"],
                      ]
                }
              />
            </Box>
          </Box>
        </AnimatedBox>
      </Animatable.View>
    </>
  );
};

export default memo(FullScreenCard);
